import React from 'react';
import { get } from 'lodash';
import { Box, Separator, Text } from '@myob/myob-widgets';
import { getTooltip } from 'utils/toolTipUtils';
import isNZProduct from 'utils/IsNZProduct';
import {
  getItemPrice,
  getProductTotalPrice,
  formatToTwoDecimalPlaces,
} from 'utils/pricingUtils';
import { ProductData } from 'hooks/useGetProduct/types';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { MinTotalPriceView } from '../minTotalPrice/MinTotalPriceView';
import { AU_PARTNER_PRODUCT_IDS, NZ_PARTNER_PRODUCT_IDS } from '../../../constants';
import { ModulePriceProps, ModulePriceTextProps, ModulePriceTextViewProps } from './type';

const getModuleProductPriceText = (gstDescription: string, moduleProductData: ProductData, productId: string) => {
  if (!moduleProductData?.items) {
    return '';
  }

  const moduleProductPrice = formatToTwoDecimalPlaces(
    getItemPrice(moduleProductData.items[0], isNZProduct(productId))
  );

  return `+ $${moduleProductPrice}/month ${gstDescription} per employee paid`;
};

const ModulePriceTextView: React.FC<ModulePriceTextViewProps> = ({
  productItems,
  moduleProductData,
  productId,
  modulePriceText,
  gstDescriptionForToolTip,
  className,
  showSeparator = false
}) => (
  <Box className={className} data-testid='module-price-text'>
    {showSeparator && (
      <Separator
        marginTop='sm'
        marginBottom='sm'
      />
    )}
    <Box display='flex'>
      <Text
        tone='neutralStrongest'
        marginBottom='clear'
        fontSize='xs'
      >
        {modulePriceText}&nbsp;
      </Text>
      {getTooltip(
        productItems[0],
        getModuleProductPriceText(gstDescriptionForToolTip, moduleProductData, productId)
      )}
    </Box>
  </Box>
);

export const ModulePriceText: React.FC<ModulePriceTextProps> = ({
  productData,
  moduleProductData,
  className
}) => {
  const productItems = get(productData, 'items');
  const productId = productItems[0]?.productId;

  if (!productId) {
    return null;
  }

  const gstDescription = isNZProduct(productId) ? '+ GST' : 'inc. GST';
  const gstDescriptionForToolTip = isNZProduct(productId) ? '(+ GST)' : '(inc. GST)';
  const modulePriceText = getModuleProductPriceText(gstDescription, moduleProductData, productId);

  if (!modulePriceText) {
    return null;
  }

  return (
    <ModulePriceTextView
      productItems={productItems}
      moduleProductData={moduleProductData}
      productId={productId}
      modulePriceText={modulePriceText}
      gstDescriptionForToolTip={gstDescriptionForToolTip}
      className={className}
    />
  );
};

export const ModulePrice: React.FC<ModulePriceProps> =
  ({
    productData,
    moduleProductData
  }) => {
    const productItems = get(productData, 'items');
    const { productId } = productItems[0];
    const { isAnnualPrice } = useSessionContext();

    const gstDescription = isNZProduct(productId) ? '+ GST' : 'inc. GST';
    const gstDescriptionForToolTip = isNZProduct(productId) ? '(+ GST)' : '(inc. GST)';
    const modulePriceText = getModuleProductPriceText(gstDescription, moduleProductData, productId);

    const minTotalPrice = formatToTwoDecimalPlaces(getProductTotalPrice(productItems));
    const isPartner = NZ_PARTNER_PRODUCT_IDS.includes(productId) || AU_PARTNER_PRODUCT_IDS.includes(productId);
    if (isPartner) {
      return (
        <MinTotalPriceView
          isAnnualPrice={isAnnualPrice}
          minTotalPrice={minTotalPrice}
          productItems={productItems}
        />
      );
    }

    if (!modulePriceText) {
      return null;
    }

    return (
      <>
        <MinTotalPriceView
          isAnnualPrice={isAnnualPrice}
          minTotalPrice={minTotalPrice}
          productItems={productItems}
        />
        <ModulePriceTextView
          productItems={productItems}
          moduleProductData={moduleProductData}
          productId={productId}
          modulePriceText={modulePriceText}
          gstDescriptionForToolTip={gstDescriptionForToolTip}
          showSeparator
        />
      </>
    );
  };
